import dayjs from 'dayjs'
import { Card } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { PlaneTakeoff, CalendarRange, CircleCheck } from '@/components/icons'
import { TaskStatusBadge, type TaskStatus } from '@/components/TaskStatusBadge'

export interface TaskTripInfoData {
  id: string
  departureAirportIcao: string
  arrivalAirportIcao: string
  departureDateTime: Date | string
  arrivalDateTime: Date | string
  trip: {
    id: string
    tripName: string
    aircraft: { registration: string }
  }
}

interface TaskTripInfoProps {
  tripLeg: TaskTripInfoData
  status?: TaskStatus
  responded?: number
  total?: number
}

export function TaskTripInfo({ tripLeg, status, responded, total }: TaskTripInfoProps) {
  const depDate = dayjs(tripLeg.departureDateTime)
  const showProgress = total !== undefined && responded !== undefined
  const progress = !total ? 0 : Math.round((responded! / total) * 100)
  const allComplete = !!total && responded === total

  return (
    <Card className="flex flex-col gap-1 px-4 py-2 border-border-L1">
      <div className="flex items-center justify-between gap-2 py-2">
        <p className="text-base font-semibold text-content-primary-bold leading-6 tracking-[-0.5px] truncate">
          {tripLeg.trip.tripName}
        </p>
        {status && <TaskStatusBadge status={status} className="shrink-0" />}
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-border-L1 pt-3 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          <PlaneTakeoff className="size-5 shrink-0 text-content-primary-bold" />
          <span className="text-sm text-content-primary-bold truncate">
            {tripLeg.departureAirportIcao} - {tripLeg.arrivalAirportIcao}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <CalendarRange className="size-5 text-content-brand-bold" />
          <span className="text-sm text-content-primary-bold">{depDate.format('DD MMM')}</span>
        </div>
      </div>

      {showProgress && (
        <div className="flex items-center gap-2 border-t border-border-L1 pt-3 pb-2">
          <Progress
            value={progress}
            className="h-1 flex-1 bg-background-brand-bold/20"
            indicatorClassName="bg-background-brand-bold"
          />
          <span className="text-xs font-semibold text-content-primary-subtle">
            {responded} of {total}
          </span>
          {allComplete && <CircleCheck className="size-5 text-content-positive-default" />}
        </div>
      )}
    </Card>
  )
}
